//agency status code company

import { Authority, useAuthenticated } from "providers/A12n"
import { CellProps, Column } from "react-table"

import { ReactComponent as CheckIcon } from "assets/check.svg"
import { ColorMap } from "utils/color"
import { ReactComponent as Deactivate } from "assets/icon-deactivate.svg"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { Link } from "components/Link"
import { MarketAgencyState } from "models/agency"
import MoreActions from "components/MoreActions"
import { MorePopover } from "components/MorePopover"
import { Status } from "components/Status"
import { css } from "@emotion/css"
import { useI18n } from "providers/I18n"

export const columns = (
  t: (k: string) => string,
  onActivate: (id: string) => void,
  onDeactivate: (id: string) => void,
  onDelete: (id: string) => void,
  hasAuthority: (allowed: Authority[]) => boolean,
): Array<Column<MarketAgencyState>> => {
  const global_columns = [
    {
      fixed: true,
      default: true,
      align: "right",
      sortable: false,
      header: "supplier-groups.list.actions",
      id: "actions",
      Cell: function (props: CellProps<MarketAgencyState>) {
        const { id, status } = props.row.original
        const { hasAuthority } = useAuthenticated()
        return (
          <MoreActions
            options={[
              {
                hidden:
                  !(status === "NEW") || !hasAuthority(["AUTH_DELETE_AGENCY"]),
                label: t("market-admin.agencies.list.delete.action"),
                icon: <DeleteIcon />,
                onClick: () => {
                  onDelete(id)
                },
              },
              {
                label: t("agency.options.activate"),
                icon: <CheckIcon />,
                hidden:
                  !(status === "NEW" || status === "INACTIVE") ||
                  !hasAuthority(["AUTH_ACTIVATE_AGENCY"]),
                onClick: () => {
                  onActivate(id)
                },
              },
              {
                label: t("deactivate"),
                icon: <Deactivate />,
                hidden:
                  !(status === "ACTIVE") ||
                  !hasAuthority(["AUTH_DEACTIVATE_AGENCY"]),
                onClick: () => {
                  onDeactivate(id)
                },
              },
            ]}
          />
        )
      },
    },
  ]

  let custom_columns = [] as any
  if (!hasAuthority(["AUTH_ACCESS_MARKET_ADMIN"])) {
    custom_columns = global_columns
  }

  return [
    {
      fixed: true,
      default: true,
      sortable: true,
      header: "agencies.list.name",
      id: "name",
      Cell: function (props: CellProps<MarketAgencyState>) {
        const { id, name, localName } = props.row.original
        return (
          <div className={agencyCell}>
            <Link color="primary" to={`/market-agencies/${id}`}>
              {name}
            </Link>
            <div>{localName}</div>
          </div>
        )
      },
    },

    {
      default: true,
      sortable: true,
      header: "agencies.list.status",
      accessor: "status",
      Cell: function (props: CellProps<MarketAgencyState>) {
        const { value: status } = props
        const { t } = useI18n()
        return status === null ? null : (
          <Status color={ColorMap[status]}>
            {t(`status.${status ?? ""}`)}
          </Status>
        )
      },
    },
    {
      default: true,
      sortable: false,
      header: "agencies.list.code",
      accessor: "code",
    },
    {
      default: true,
      sortable: false,
      header: "agencies.list.company",
      accessor: "companies",
      Cell: function (props: CellProps<MarketAgencyState>) {
        const { companies } = props.row.original
        const companiesToShow = companies?.slice(0, 1)?.map((c) => c?.name)
        return (
          <div>
            {companiesToShow}
            <MorePopover
              data={companies}
              label="market-agencies.companies"
              name="name"
            />
          </div>
        )
      },
    },
    ...custom_columns,
  ]
}

export const filters = []
export const sortByDefault = "updated_date,DESC"

const agencyCell = css`
  display: flex;
  flex-direction: column;
  div {
    color: var(--dark-grey);
    font-size: 12px;
  }
`
